import { memo, useCallback } from 'react';

import { useTrackClickedNotificationsIcon } from '@air/analytics';
import { BellIcon } from '@air/design-icons';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useGetNotificationSummary } from '~/components/AirNotifications/hooks/useGetNotificationSummary';
import { useResetUnviewedCount } from '~/components/AirNotifications/hooks/useResetUnviewedCount';
import { MainSideNavItemButton } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemButton';
import { MainSideNavItemCountBadge } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemCountBadge';
import { MainSideNavItemIcon } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemIcon';
import { MainSideNavItemLabel } from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItemLabel';
import { useMainSideNavOverlay } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlay';

export const MainSideNavItemNotifications = memo(() => {
  const { data: notificationSummary } = useGetNotificationSummary();
  const { resetUnviewedCount } = useResetUnviewedCount();
  const { isOpen, onOpenChange } = useMainSideNavOverlay({ id: 'notifications' });
  const { trackClickedNotificationsIcon } = useTrackClickedNotificationsIcon();
  const unseenCount = notificationSummary?.unseen ?? 0;

  const handleClick = useCallback(() => {
    if (!isOpen) {
      resetUnviewedCount().catch((error) => {
        reportErrorToBugsnag({ error, context: 'Failed to reset unviewed notification count' });
      });
      trackClickedNotificationsIcon({ unviewedNotifications: notificationSummary?.unseen });
    }

    onOpenChange(!isOpen);
  }, [isOpen, notificationSummary?.unseen, onOpenChange, resetUnviewedCount, trackClickedNotificationsIcon]);

  return (
    <MainSideNavItemButton
      data-item-id="notifications"
      data-main-side-nav-overlay
      data-state={isOpen ? 'open' : undefined}
      data-testid="AIR_NOTIFICATIONS_BUTTON"
      onClick={handleClick}
      type="button"
    >
      <MainSideNavItemIcon>
        <span className="relative">
          <BellIcon className="shrink-0" size="sm" />
          {unseenCount > 0 ? (
            <MainSideNavItemCountBadge count={unseenCount} data-testid="AIR_NOTIFICATIONS_BUTTON_BADGE" />
          ) : null}
        </span>
      </MainSideNavItemIcon>
      <MainSideNavItemLabel>Notifications</MainSideNavItemLabel>
    </MainSideNavItemButton>
  );
});

MainSideNavItemNotifications.displayName = 'MainSideNavItemNotifications';
